import { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import { getDocumentById, getSignedUrl, deleteDocument } from 'api/documents';

import { getBytes } from 'helpers/files';
import { formatDatetime } from 'helpers/timezone';
import {
  downloadDocument,
  shouldGeneratePreview,
  displaySignButton,
} from 'helpers/document';

import { TYPES, SIGNATURE_STATUS } from 'config/document';

import CustomerCard from 'components/cards/customer';
import ViewField from 'components/view_field/view_field';

import NoAccess from 'pages/no_access';
import Deleted from 'components/deleted/deleted';

import Loading from 'components/loading/loading';

import DocumentPreview from 'components/document/document_preview';
import Modal from 'components/modal/delete';

const DocumentItem = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [blobUrl, setBlobURL] = useState(null);
  const [deletedByData, setDeletedByData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(null);

  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const fetchData = useCallback(async () => {
    setLoading(true);

    const document = await getDocumentById(id);

    if (document.size < 5000000 && shouldGeneratePreview(document.path)) {
      //show preview for small files with path only
      const { url } = await getSignedUrl(id);

      const fileResponse = await fetch(url);
      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      setBlobURL(blobUrl);
    }

    if (document.deletedAt && document.deletedBy) {
      setDeletedByData({
        email: document.deletedBy.email,
        deletedAt: document.deletedAt,
      });
    } else {
      setData(document);
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    (async () => fetchData())();
  }, [fetchData]);

  const onDownload = async () => {
    await downloadDocument(id, data);
  };
  const onDelete = async () => {
    await deleteDocument(id);

    const doesAnyHistoryEntryExist = location.key;
    if (doesAnyHistoryEntryExist) {
      history.goBack();
    } else {
      history.push('/');
    }
  };

  if (loading) return <Loading />;
  if (data?.statusCode === 403) return <NoAccess />;
  if (deletedByData) return <Deleted deletedBy={deletedByData} />;

  let createdBy;

  if (data.user) {
    const role = data.user.role;
    createdBy = [data.user[role].firstName, data.user[role].lastName]
      .filter(Boolean)
      .join(' ');
  }

  const shouldDisplayEditButton = !displaySignButton(data);

  return (
    <>
      <main className="th-main">
        <section className="th-canvas">
          <section className="view-container">
            <div className="th-canvas-header">
              <div>
                <h3 className="f2 fw6 ma0 pa0">
                  {TYPES.find((t) => t.id === data.type).label}
                </h3>
                <p className="f7 pa0 ma0 midlightgrey-d1">
                  <span className="darkgrey fw5">
                    {data.path}, {getBytes(data.size)}
                  </span>
                </p>
              </div>
              <section className="view-actions">
                <button
                  className="btn-red mr5"
                  data-cy="delete"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <span>Delete</span>
                </button>

                {shouldDisplayEditButton && (
                  <button
                    className="mr5 btn-white"
                    data-cy="edit"
                    onClick={() => history.push(`/documents/${id}/edit`)}
                  >
                    Edit
                  </button>
                )}

                <button
                  className="btn-white"
                  data-cy="download"
                  onClick={onDownload}
                >
                  Download
                </button>

                {data.type === 'dap' && data.isGenerated && (
                  <button
                    className="ml5 btn-white"
                    data-cy="update"
                    onClick={() => history.push(`/documents/${id}/update`)}
                  >
                    Update fields
                  </button>
                )}
              </section>
            </div>

            <div className="flex flex-column items-center mb10 pt8">
              <div className="w-100 flex flex-column items-start justify-between">
                <ViewField
                  label="Created at"
                  value={formatDatetime(data.createdAt)}
                />

                <ViewField label="Created by" value={createdBy} />

                <ViewField
                  label="Signature status"
                  value={
                    SIGNATURE_STATUS.find((s) => s.id === data.signatureStatus)
                      ?.label
                  }
                />

                <ViewField
                  label="Patient's access"
                  value={
                    data.isShared
                      ? 'Shared with patient'
                      : 'Not shared with patient'
                  }
                />

                <CustomerCard data={data.customer} />

                {blobUrl && <DocumentPreview blobUrl={blobUrl} />}
              </div>
            </div>
          </section>
        </section>
      </main>
      {deleteModalOpen && (
        <Modal
          title="Delete document"
          content="Are you sure you woulld like to delete this document?"
          onClose={() => setDeleteModalOpen(false)}
          onClickOutside={() => setDeleteModalOpen(false)}
          onDelete={onDelete}
        />
      )}
    </>
  );
};

export default DocumentItem;
